<script setup lang="ts" generic="T extends BaseType">
import type { BaseType, PasswordProtectedResponse } from '~/types'

defineOptions({
  name: 'UIPasswordProtected',
})

interface Props {
  data: PasswordProtectedResponse<T>
  entityName?: string
}

interface Emits {
  (event: 'submit', password?: string): void
}

const props = withDefaults(defineProps<Props>(), {
  entityName: 'page',
})
const emit = defineEmits<Emits>()

const password = ref<string>('')

const handleRequest = (password?: string): void => {
  if (!password) {
    return
  }

  emit('submit', password)
}

const {
  data,
  error,
  isIncorrectPassword,
  isPasswordRequired,
} = usePasswordProtection<T>(props.data)
</script>

<template>
  <section
    v-if="isPasswordRequired"
    class="password-protected"
  >
    <Wrapper class="password-protected__wrapper">
      <UIDynamicHeading class="password-protected__title">
        Password required
      </UIDynamicHeading>
      <p class="password-protected__text">
        To view this protected {{ entityName }}, enter the password below:
      </p>
      <UIPasswordProtectedForm
        v-model="password"
        class="password-protected__form"
        :class="{ 'password-protected__form--invalid': isIncorrectPassword }"
        @submit.prevent="handleRequest(password)"
      />
    </Wrapper>
  </section>
  <slot
    v-else
    :data
    :error
  />
</template>

<style lang="scss" scoped>
.password-protected {
  @apply my-8;
  @apply md:my-16;
  @apply lg:my-24;
  @apply xl:my-48;

  &__wrapper {
    @apply flex flex-col items-center;
  }

  &__title {
    @apply mb-2;
  }

  &__text {
    @apply text-[14px] text-center leading-[1.5] mb-6;
    @apply lg:text-[22px];
  }

  &__form {
    &--invalid {
      animation: shake 0.2s ease-in-out 0s 2;
    }
  }
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(0.5rem);
  }
  75% {
    transform: translateX(-0.5rem);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
